import { atom, computed, map } from "nanostores";

import { UIGraphEdge, UIGraphElement, UIGraphNode } from "../../types/graph";
import { $router } from "../router.ts";
import { $cytoscapeInstance } from "./ui.ts";

interface SelectedItemConnections {
  incoming: UIGraphNode["data"][];
  incomingEdges: UIGraphEdge["data"][];
  outgoing: UIGraphNode["data"][];
}

// Internal raw selection store - this is the raw UI components, includes groups
const $rawSelectedElements = atom<Array<UIGraphElement["data"]>>([]);

// Computed store that expands group selections whether collapsed or expanded into their children and removes the groups themselves
export const $selectedElements = computed(
  [$rawSelectedElements, $cytoscapeInstance],
  (rawElements, cytoscapeInstance) => {
    if (!rawElements.length || !cytoscapeInstance) {
      return rawElements;
    }

    const processedElements: Array<UIGraphElement["data"]> = [];

    const getElementKey = (element: UIGraphElement["data"]): string => {
      // De-dupe by element type + id (node ids can overlap edge ids in some graphs)
      return `${element.type}:${element.id}`;
    };

    for (const element of rawElements) {
      const cytoscapeNode = cytoscapeInstance.getElementById(element.id);

      // Check if this is a group/cluster node (parent node with children) or collapsed
      if (cytoscapeNode.length > 0) {
        const isParentNode = cytoscapeNode.isParent && cytoscapeNode.isParent();
        const nodeData = cytoscapeNode.data();
        // collapsed nodes are not considered parents BUT do have collapsedChildren data
        const hasChildren =
          cytoscapeNode.children().length > 0 ||
          (nodeData.collapsedChildren && nodeData.collapsedChildren.length > 0);

        if (isParentNode || hasChildren) {
          let childElements: unknown[] = [];

          if (isParentNode) {
            // For expanded parent nodes, get children normally
            childElements = cytoscapeNode.children().toArray();
          } else if (hasChildren) {
            // For collapsed nodes, get children from collapsed data
            childElements = Array.from(nodeData.collapsedChildren);
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const expandedChildren = childElements.map((child: any) => {
            const childData = child.data ? child.data() : child;
            return {
              ...childData,
            };
          });

          processedElements.push(...expandedChildren);
        } else {
          processedElements.push(element);
        }
      } else {
        processedElements.push(element);
      }
    }

    // Cytoscape selection can include both group nodes and their children, and we also expand
    // group selections into children. De-dupe to avoid double-counting in the UI.
    const uniqueElementsMap = new Map<string, UIGraphElement["data"]>();
    for (const element of processedElements) {
      const key = getElementKey(element);
      if (!uniqueElementsMap.has(key)) {
        uniqueElementsMap.set(key, element);
      }
    }

    return Array.from(uniqueElementsMap.values());
  },
);

export const $selectedItemConnections = map<SelectedItemConnections>({
  incoming: [],
  incomingEdges: [],
  outgoing: [],
});

$router.subscribe(() => {
  $rawSelectedElements.set([]);

  $selectedItemConnections.set({
    incoming: [],
    incomingEdges: [],
    outgoing: [],
  });
});

// Export function to set selected elements (for use by event handlers)
export const setSelectedElements = (
  elements: Array<UIGraphElement["data"]>,
): void => {
  $rawSelectedElements.set(elements);
};
